import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { friendsChanged } from "@/lib/friends";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import { toast } from "sonner";
import { ChatCircleDots, Check, X, UserPlus, MagnifyingGlass } from "@phosphor-icons/react";

const small = "inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-bold uppercase tracking-wider disabled:opacity-50";
const primary = `${small} bg-brand text-brand-foreground hover:bg-brand-hover`;
const quiet = `${small} border border-border text-muted-foreground hover:text-foreground`;

function Row({ f, children, testId }) {
  return (
    <li className="flex items-center gap-3 py-2.5 border-t border-border first:border-t-0" data-testid={testId}>
      <Avatar userId={f.id} username={f.username} version={f.avatar_v ?? null} className="w-9 h-9 text-sm" />
      <div className="flex-1 min-w-0">
        <PlayerLink id={f.id} className="font-semibold truncate block">{f.username}</PlayerLink>
        <span className="text-xs text-muted-foreground">{f.elo_rating} Elo</span>
      </div>
      <div className="flex items-center gap-2 flex-none">{children}</div>
    </li>
  );
}

// Your friends on your own profile: requests to answer, friends to message,
// and a username search to add someone new.
export default function FriendsCard() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const searchSeq = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/friends");
      setData(d);
    } catch { /* keep what we have */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState !== "hidden" && load(), 30000);
    return () => clearInterval(t);
  }, [load]);

  const loaded = data != null;
  useEffect(() => {
    if (hash === "#friends" && loaded) {
      document.getElementById("friends")?.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [hash, loaded]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const seq = ++searchSeq.current;
    const t = setTimeout(async () => {
      try {
        const { data: users } = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
        if (seq === searchSeq.current) setResults(users || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const act = async (key, fn, done) => {
    setBusy(key);
    try {
      const { data: r } = await fn();
      if (done) toast.success(done(r.status));
      friendsChanged();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  const known = new Set(
    data ? [...data.friends, ...data.incoming, ...data.outgoing].map((f) => f.id) : [],
  );
  const relationOf = (id) =>
    data?.friends.some((f) => f.id === id) ? "Friends"
      : data?.outgoing.some((f) => f.id === id) ? "Request sent"
      : data?.incoming.some((f) => f.id === id) ? "Wants to be friends" : null;

  return (
    <div id="friends" className="bg-surface border border-border rounded-2xl p-6 mb-8 scroll-mt-20" data-testid="friends-card">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="font-display text-xl font-bold tracking-tight">Friends</h3>
        {data?.friends.length ? (
          <span className="text-xs text-muted-foreground">{data.friends.length}</span>
        ) : null}
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Add a friend by username"
          className="w-full h-10 pl-9 pr-3 rounded-full bg-background border border-border text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          data-testid="friend-search"
        />
      </div>
      {results.length > 0 && (
        <ul className="mb-4 rounded-xl border border-border px-3" data-testid="friend-search-results">
          {results.map((u) => (
            <Row key={u.id} f={u} testId={`friend-search-${u.username}`}>
              {known.has(u.id) ? (
                <span className="text-xs text-muted-foreground">{relationOf(u.id)}</span>
              ) : (
                <button
                  type="button"
                  disabled={busy === `add:${u.id}`}
                  className={primary}
                  onClick={() => act(`add:${u.id}`, () => api.post(`/friends/${u.id}/request`),
                    (s) => (s === "friends" ? `You and ${u.username} are friends` : `Friend request sent to ${u.username}`))}
                  data-testid={`friend-search-add-${u.username}`}
                >
                  <UserPlus size={14} weight="bold" /> Add
                </button>
              )}
            </Row>
          ))}
        </ul>
      )}

      {!data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {data.incoming.length > 0 && (
            <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">Friend requests</div>
              <ul>
                {data.incoming.map((f) => (
                  <Row key={f.id} f={f} testId={`friend-incoming-${f.username}`}>
                    <button
                      type="button"
                      disabled={busy === `acc:${f.id}`}
                      className={primary}
                      onClick={() => act(`acc:${f.id}`, () => api.post(`/friends/${f.id}/accept`),
                        () => `You and ${f.username} are friends`)}
                      data-testid={`friend-accept-${f.username}`}
                    >
                      <Check size={14} weight="bold" /> Accept
                    </button>
                    <button
                      type="button"
                      disabled={busy === `dec:${f.id}`}
                      className={quiet}
                      onClick={() => act(`dec:${f.id}`, () => api.post(`/friends/${f.id}/decline`))}
                      aria-label={`Decline ${f.username}`}
                      data-testid={`friend-decline-${f.username}`}
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </Row>
                ))}
              </ul>
            </div>
          )}

          {data.friends.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No friends yet. Search for a username above, or press Add friend on any player's profile.
            </p>
          ) : (
            <ul data-testid="friends-list">
              {data.friends.map((f) => (
                <Row key={f.id} f={f} testId={`friend-${f.username}`}>
                  <button
                    type="button"
                    className={primary}
                    onClick={() => navigate(`/friends/${f.id}`)}
                    data-testid={`friend-chat-${f.username}`}
                  >
                    <ChatCircleDots size={14} weight="bold" /> Chat
                    {f.unread > 0 && (
                      <span className="ml-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-destructive text-white text-[11px] leading-5 text-center">
                        {f.unread}
                      </span>
                    )}
                  </button>
                </Row>
              ))}
            </ul>
          )}

          {data.outgoing.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">Requests you sent</div>
              <ul>
                {data.outgoing.map((f) => (
                  <Row key={f.id} f={f} testId={`friend-outgoing-${f.username}`}>
                    <button
                      type="button"
                      disabled={busy === `can:${f.id}`}
                      className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:underline"
                      onClick={() => act(`can:${f.id}`, () => api.delete(`/friends/${f.id}`))}
                      data-testid={`friend-cancel-${f.username}`}
                    >
                      Cancel
                    </button>
                  </Row>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
